import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { TypographyLarge, TypographyNormal } from "../customComponent";
import GroupItem from "./GroupItem";
import { getGroups } from "../../store/actions/groupAction";
import { colorStyling } from "../../helpers/color";
import { filterObjtoString } from "../../helpers/filterHelper";
import {
  SET_GROUP_PAGE,
  SET_GROUP_ROWS_PER_PAGE,
  SET_LOADING_GROUP,
} from "../../store/actionTypes/groupActionType";
import { parseVariables } from "../../helpers/localize";

const tableStyling = {
  padding: "12px 12px",
};

const GroupList = () => {
  const dispatch = useDispatch();
  const { groups, loadingGroup, groupFilters, page, rowsPerPage } = useSelector(
    (state) => state.group
  );

  const { t } = useTranslation("group");
  const { t: tCommon } = useTranslation();

  const scrollRef = useRef(null);
  const fetchOnFirstLoad = useRef(false); // Track whether data has been fetched on first load

  // TODO for
  // how many prefetched data page rows
  // step 1 = 5 data (if rows per page = 5)
  // const step = 1;

  const handleLoading = (value) => {
    dispatch({ type: SET_LOADING_GROUP, payload: value });
  };

  const handleChangePagination = (e, page) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    dispatch({ type: SET_GROUP_PAGE, payload: page });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: SET_GROUP_ROWS_PER_PAGE,
      payload: parseInt(event.target.value, 10),
    });
    handleChangePagination(null, 0);
  };

  const fetchData = async (filterObj) => {
    let isPrefetch = false;
    let limit = rowsPerPage;
    let offset = page * rowsPerPage;
    if (offset === 0) {
      limit = limit * 2; //fetch 2 page worth of data
      isPrefetch = false;
    } else {
      offset = (page + 1) * rowsPerPage;
      isPrefetch = true;
    }
    let filterString = filterObjtoString(filterObj, limit, offset);

    const groupsLength = groups?.items?.length;
    if (groupsLength === 0) handleLoading(true);
    if (groupsLength !== groups.totalItem || groupsLength === 0 || offset === 0)
      dispatch(getGroups(filterString, isPrefetch, handleLoading));
  };

  useEffect(() => {
    if (fetchOnFirstLoad.current) {
      fetchData(groupFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchData(groupFilters);
      fetchOnFirstLoad.current = true;
    } else handleChangePagination(null, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupFilters]);

  const displayedRowsLabel = ({ from, to, count }) => {
    return parseVariables(tCommon("table.displayedRowsLabel"), {
      prefix: `${from}–${to}`,
      suffix:
        count !== -1
          ? count
          : `${tCommon("table.displayedRowsMoreThan")} ${to}`,
    });
  };

  return (
    <>
      <Box>
        {loadingGroup ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ height: "20vh" }}
          >
            <CircularProgress
              size={20}
              thickness={3}
              sx={{ color: colorStyling.primary }}
            />
            <TypographyNormal
              sx={{ color: colorStyling.primary, mt: 2, fontWeight: 300 }}
            >
              {tCommon("loadingDataList")}
            </TypographyNormal>
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              elevation={2}
              sx={{
                overflowY: "auto",
                maxHeight: 500,
                borderRadius: "6px 6px 0 0",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...tableStyling, paddingLeft: 4 }}>
                      {t("table.headerGroupName")}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ ...tableStyling, width: 180 }}
                    >
                      {t("table.headerViewers")}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...tableStyling, width: 240, pr: "44px" }}
                    >
                      <Box
                        display={"flex"}
                        justifyContent={"space-around"}
                        alignItems={"center"}
                      >
                        <span style={{ marginRight: "4px" }}>
                          {t("table.headerSchedules")}
                        </span>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                {groups.totalItem !== 0 && (
                  <TableBody>
                    {groups?.items?.length !== 0 ? (
                      groups?.items
                        ?.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                        ?.map((group, index) => (
                          <GroupItem
                            index={index}
                            key={group.id}
                            group={group}
                            dispatch={dispatch}
                          />
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Box
                            sx={{ height: "20vh" }}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            flexDirection="column"
                          >
                            <CircularProgress
                              size={20}
                              thickness={3}
                              sx={{ color: colorStyling.primary }}
                            />
                            <TypographyNormal
                              sx={{
                                color: colorStyling.primary,
                                mt: 2,
                                fontWeight: 400,
                              }}
                            >
                              {tCommon("loadingDataList")}
                            </TypographyNormal>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>

              {groups?.items?.length === 0 && (
                <Box
                  alignItems={"center"}
                  bgcolor={colorStyling.cms.white}
                  sx={{ p: 5 }}
                >
                  <TypographyLarge textAlign={"center"}>
                    {t("table.noData")}
                  </TypographyLarge>
                </Box>
              )}
            </TableContainer>
          </>
        )}
      </Box>

      {(!loadingGroup || groups?.totalItem > 0) && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            pr: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Paper component="div" elevation={1} sx={{ px: 1 }}>
            <TablePagination
              component="div"
              count={groups.totalItem}
              page={page}
              onPageChange={handleChangePagination}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={displayedRowsLabel}
              labelRowsPerPage={tCommon("table.rowsPerPageLabel")}
            />
          </Paper>
        </Box>
      )}
    </>
  );
};

export default GroupList;
